// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, pipe, zip } from 'rxjs';
import { filter, map, mergeMap, take, withLatestFrom } from 'rxjs/operators';

import { SelectablesResponseVehicleComponent, VehicleComponent } from 'src/app/api/owcc/models';
import { ActivatedRouteState } from 'src/app/core/services/activated-route/activated-route-state.interface';
import {
  selectConfigurationSelectableCategories,
  selectConfigurationSelectableComponentsOrUndefined
} from 'src/app/store/common/common.selectors';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';
import { EnrichedComponentCategory } from 'src/app/utils/models/enriched-component-category.type';
import { VehicleSelectableChange } from 'src/app/utils/models/vehicle-selectable-change.enum';

import { AppRoutePath } from '../../routing/app-route-path.enum';
import { selectRouteState } from '../../store/router/router.selectors';
import { VehicleComponentCollection } from '../../utils/models/vehicle-component-collection.interface';

import { startEnqueueSelectableChangeAction } from './store/configuration.actions';

const getAppRoutePathByParams = (
  isPreconfig: boolean,
  isVehicleLength: boolean,
  isMotorization: boolean,
  configurationStep: string
): string => {
  if (isPreconfig) {
    return AppRoutePath.Preconfig;
  }
  if (isVehicleLength) {
    return AppRoutePath.VehicleLength;
  }
  if (isMotorization) {
    return AppRoutePath.Motorization;
  }
  return configurationStep;
};

const selectCurrentCategory = pipe(
  select(selectRouteState),
  filter(
    (routeState: ActivatedRouteState) =>
      routeState?.isConfigurationStep || routeState?.isVehicleLength || routeState?.isMotorization
  ),
  map(({ isPreconfig, isVehicleLength, isMotorization, configurationStep }) =>
    getAppRoutePathByParams(isPreconfig, isVehicleLength, isMotorization, configurationStep)
  )
);

const selectCurrentNavigationStep = pipe(
  select(selectRouteState),
  filter((routeState: ActivatedRouteState) => !!routeState),
  map(({ isPreconfig, isVehicleLength, isMotorization, configurationStep }) =>
    getAppRoutePathByParams(isPreconfig, isVehicleLength, isMotorization, configurationStep)
  )
);

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  public constructor(private readonly store: Store) {}

  public get currentSelectedCategory$(): Observable<EnrichedComponentCategory> {
    return this.store.pipe(
      selectCurrentCategory,
      mergeMap(navigationStep => this.getSelectableCategory(navigationStep?.toUpperCase()))
    );
  }

  public get currentNavigatedCategory$(): Observable<EnrichedComponentCategory> {
    return this.store.pipe(
      selectCurrentNavigationStep,
      mergeMap(navigationStep => this.getSelectableCategory(navigationStep?.toUpperCase()))
    );
  }

  /**
   * Returns flat collection of selectable components
   *
   * USE ON ALL CONFIGURATION STEPS EXCEPT SUMMARY !!!
   */
  private get selectableComponents$(): Observable<VehicleComponentCollection> {
    return this.store.select(selectConfigurationSelectableComponentsOrUndefined);
  }

  /**
   * Returns collection of selectable categories with included components
   *
   * USE ON ALL CONFIGURATION STEPS EXCEPT SUMMARY !!!
   */
  private get selectableCategories$(): Observable<EnrichedComponentCategory[]> {
    return this.store.select(selectConfigurationSelectableCategories).pipe(filterTruthy());
  }

  public getSelectableCategory(categoryId: string): Observable<EnrichedComponentCategory | null> {
    return this.selectableCategories$.pipe(
      map(categories => {
        // check if categories if categories is array
        if (!categories || !Array.isArray(categories)) {
          return null;
        }

        const navigationGroup = categories.find(category => category?.id?.toUpperCase() === categoryId?.toUpperCase());
        if (navigationGroup) {
          return navigationGroup;
        }

        for (const category of categories) {
          const resolvedSubcategory = category.subcategories
            ? category.subcategories.find(subcategory => subcategory?.id?.toUpperCase() === categoryId?.toUpperCase())
            : null;
          if (resolvedSubcategory) {
            return resolvedSubcategory;
          }
        }

        return null;
      })
    );
  }

  /**
   * Find the associated 2nd level category for a given component id
   * It walks the category path downwards until the second level and returns that category
   * Example:
   *  Path: [EXTERIEUR, LINES, PC-123]:
   *
   * @returns a string with the current category, for example'LINES'
   */
  public getSecondLevelCategoryOfComponent(componentId: string): Observable<EnrichedComponentCategory> {
    return this.selectableComponents$.pipe(
      map(
        (components: VehicleComponentCollection = {}): VehicleComponent | SelectablesResponseVehicleComponent =>
          components[componentId] ?? null
      ),
      filterTruthy(),
      withLatestFrom(this.selectableCategories$),
      map(
        ([component, selectableCategories]: [
          VehicleComponent,
          EnrichedComponentCategory[]
        ]): EnrichedComponentCategory => {
          let categories: EnrichedComponentCategory[] = selectableCategories;
          let foundCategory: EnrichedComponentCategory;
          const path: string[] = [...component.path];

          let counter = 0;
          while (categories.length > 0 && counter <= 1) {
            const partialPath = path.shift();
            counter = counter + 1;

            const currentCategory: EnrichedComponentCategory = categories.find(
              (category: EnrichedComponentCategory): boolean => category.id === partialPath
            );

            foundCategory = currentCategory;

            if (currentCategory?.subcategories?.length) {
              categories = currentCategory.subcategories;
            }
          }

          return foundCategory;
        }
      )
    );
  }

  public getCategoriesOfFixationByComponent(category: EnrichedComponentCategory): Observable<string[]> {
    const observables: Observable<EnrichedComponentCategory>[] =
      category.fixationInformation?.filteredBy?.map(
        (componentId: string): Observable<EnrichedComponentCategory> =>
          this.getSecondLevelCategoryOfComponent(componentId)
      ) || [];

    return zip(...observables).pipe(
      take(1),
      map((fixedByCategories: EnrichedComponentCategory[]) =>
        fixedByCategories.map((fixCategory: EnrichedComponentCategory): string => fixCategory.name ?? fixCategory.id)
      )
    );
  }

  public addSelectable(component: string | VehicleComponent, source: string): void {
    this.changeSelectable(VehicleSelectableChange.Add, component, source);
  }

  public removeSelectable(component: string | VehicleComponent, source: string): void {
    this.changeSelectable(VehicleSelectableChange.Remove, component, source);
  }

  private changeSelectable(
    change: VehicleSelectableChange,
    component: string | VehicleComponent,
    source: string
  ): void {
    const componentId = typeof component === 'string' ? component : component.id;
    if (!componentId) {
      return;
    }
    this.store.dispatch(
      startEnqueueSelectableChangeAction({
        selectableChange: { change, componentId },
        source
      })
    );
  }
}
